import { ApiModel } from '@composable-api/api.model'
import { ProductModel } from './product.model'
import { ProductVariationModel } from './product-variation.model'

interface Attributes {
    [ProductRelatedProductModel.ATTR_ID]: number
    [ProductRelatedProductModel.ATTR_MAIN_PRODUCT_ID]: number
    [ProductRelatedProductModel.ATTR_MAIN_PRODUCT_VARIATION_ID]: number
    [ProductRelatedProductModel.ATTR_RELATED_PRODUCT_ID]: number
    [ProductRelatedProductModel.ATTR_RELATED_PRODUCT_VARIATION_ID]: number
    [ProductRelatedProductModel.ATTR_RELATION_TYPE]: string
    [ProductRelatedProductModel.ATTR_POSITION]: number
}

interface Embeds {
    [ProductRelatedProductModel.EMBED_RELATED_PRODUCT_NAME]?: string | null
    [ProductRelatedProductModel.EMBED_MAIN_PRODUCT]?: ProductModel
    [ProductRelatedProductModel.EMBED_MAIN_PRODUCT_VARIATION]?: ProductVariationModel
    [ProductRelatedProductModel.EMBED_RELATED_PRODUCT]?: ProductModel
    [ProductRelatedProductModel.EMBED_RELATED_PRODUCT_VARIATION]?: ProductVariationModel
}

export class ProductRelatedProductModel extends ApiModel<Attributes, Embeds> {
    static key = 'ProductRelatedProductModel'

    static readonly ATTR_ID = 'id'
    static readonly ATTR_MAIN_PRODUCT_ID = 'main_product_id'
    static readonly ATTR_MAIN_PRODUCT_VARIATION_ID = 'main_product_variation_id'
    static readonly ATTR_RELATED_PRODUCT_ID = 'related_product_id'
    static readonly ATTR_RELATED_PRODUCT_VARIATION_ID = 'related_product_variation_id'
    static readonly ATTR_RELATION_TYPE = 'relation_type'
    static readonly ATTR_POSITION = 'position'

    static readonly EMBED_RELATED_PRODUCT_NAME = 'related_product_name'
    static readonly EMBED_MAIN_PRODUCT = 'main_product'
    static readonly EMBED_MAIN_PRODUCT_VARIATION = 'main_product_variation'
    static readonly EMBED_RELATED_PRODUCT = 'related_product'
    static readonly EMBED_RELATED_PRODUCT_VARIATION = 'related_product_variation'

    get id() {
        return this._getAttribute(ProductRelatedProductModel.ATTR_ID)
    }

    /**
     * This is the ID of the product, to which this related product belongs.
     * For example, if this related product pairing is of type gift, this would be the ID of the main product
     * that has a gift, which is identified by `relatedProductId`
     * @see relatedProductId
     */
    get mainProductId() {
        return this._getAttribute(ProductRelatedProductModel.ATTR_MAIN_PRODUCT_ID)
    }

    /**
     * This is the ID of the product variation, to which this related product belongs.
     */
    get mainProductVariationId() {
        return this._getAttribute(ProductRelatedProductModel.ATTR_MAIN_PRODUCT_VARIATION_ID)
    }

    /**
     * This is the ID of the related ProductModel.
     * For example, if this related product pairing is of type gift, this would be the ID of the gift product.
     */
    get relatedProductId() {
        return this._getAttribute(ProductRelatedProductModel.ATTR_RELATED_PRODUCT_ID)
    }

    /**
     * This is the ID of the related ProductVariationModel.
     * For example, if this related product pairing is of type gift, this would be the ID of the gift product variation.
     */
    get relatedProductVariationId() {
        return this._getAttribute(ProductRelatedProductModel.ATTR_RELATED_PRODUCT_VARIATION_ID)
    }

    get relationType() {
        return this._getAttribute(ProductRelatedProductModel.ATTR_RELATION_TYPE)
    }

    get position() {
        return this._getAttribute(ProductRelatedProductModel.ATTR_POSITION)
    }

    get relatedProductName() {
        return this._getEmbed(ProductRelatedProductModel.EMBED_RELATED_PRODUCT_NAME)
    }

    get mainProduct() {
        return this._getEmbed(ProductRelatedProductModel.EMBED_MAIN_PRODUCT, ProductModel)
    }

    get mainProductVariation() {
        return this._getEmbed(ProductRelatedProductModel.EMBED_MAIN_PRODUCT_VARIATION, ProductVariationModel)
    }

    get relatedProduct() {
        return this._getEmbed(ProductRelatedProductModel.EMBED_RELATED_PRODUCT, ProductModel)
    }

    get relatedProductVariation() {
        return this._getEmbed(ProductRelatedProductModel.EMBED_RELATED_PRODUCT_VARIATION, ProductVariationModel)
    }

    // ---------------------------------------------------------------------------------------------------------------------

    /**
     * Check whether this related product is related to a product variation.
     * If `variationId` is provided, it checks whether this is related to that specific variation.
     * @param variationId optional ID of the variation to check against
     */
    isForVariation(variationId?: number): boolean {
        if (variationId) return this.mainProductVariationId === variationId
        return this.mainProductVariationId !== null
    }

}
